import {
  CONTRAST_STORAGE_KEY,
  THEME_STORAGE_KEY,
  isContrastPreference,
  isThemePreference,
  resolveTheme,
  themeAttributes,
  type ContrastPreference,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

/**
 * 外观状态是一个**模块级单例**，不是每个组件自己的 `useState`。
 *
 * 理由很直接：标题栏的切换按钮、设置页的同名控件、以及将来任何需要知道
 * "现在是深色还是浅色"的地方（地图样式、图表配色）都必须看到同一份状态。
 * 用 `useState` 的话点标题栏的按钮设置页不会跟着变 —— 这类 bug 在 UI 上很隐蔽。
 *
 * 因此这里是一个最小的外部 store（`useSyncExternalStore` 可直接消费），
 * 不引入 zustand：`@hgc/ui` 不允许依赖状态库，也不该为二十行状态引一个。
 */

export interface ThemeState {
  readonly preference: ThemePreference;
  readonly contrast: ContrastPreference;
  readonly systemPrefersDark: boolean;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

let state: ThemeState = { preference: 'system', contrast: 'normal', systemPrefersDark: false };
const listeners = new Set<() => void>();
let initialized = false;

/**
 * `localStorage` 在隐私模式或受限 iframe 里可能直接抛异常。
 * 外观读不出来只是"回到默认外观"，不该让整个应用起不来。
 */
function readStored<T>(key: string, guard: (value: unknown) => value is T, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return guard(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 存不下只影响下次启动的默认值，不影响本次会话 */
  }
}

function systemPrefersDarkNow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

/**
 * 把状态写进 `<html>`。
 *
 * `index.html` 的内联首屏脚本复刻了同一套规则（见 `themeAttributes`），
 * 目的是在样式生效前就定好外观，避免深色下闪一下白。
 */
function applyToDocument(): void {
  if (typeof document === 'undefined') return;
  const attributes = themeAttributes(state.preference, state.contrast, state.systemPrefersDark);
  const root = document.documentElement;
  root.classList.toggle('dark', attributes.dark);
  if (attributes.contrast === 'normal') root.removeAttribute('data-contrast');
  else root.setAttribute('data-contrast', attributes.contrast);
}

function update(patch: Partial<ThemeState>): void {
  const next: ThemeState = { ...state, ...patch };
  if (
    next.preference === state.preference &&
    next.contrast === state.contrast &&
    next.systemPrefersDark === state.systemPrefersDark
  ) {
    return;
  }
  state = next;
  applyToDocument();
  for (const listener of listeners) listener();
}

function handleSystemChange(event: MediaQueryListEvent): void {
  // 用户显式选了浅色/深色时，系统变化与本应用无关，不该触发重渲染
  if (state.preference !== 'system') return;
  update({ systemPrefersDark: event.matches });
}

function initialize(): void {
  if (initialized) return;
  initialized = true;

  state = {
    preference: readStored(THEME_STORAGE_KEY, isThemePreference, 'system'),
    contrast: readStored(CONTRAST_STORAGE_KEY, isContrastPreference, 'normal'),
    systemPrefersDark: systemPrefersDarkNow(),
  };
  applyToDocument();

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    window.matchMedia(DARK_QUERY).addEventListener('change', handleSystemChange);
  }
}

export function subscribeTheme(listener: () => void): () => void {
  initialize();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 供 `useSyncExternalStore` 使用：状态对象只在真正变化时被替换，引用保持稳定。 */
export function getThemeState(): ThemeState {
  initialize();
  return state;
}

export function setThemePreference(preference: ThemePreference): void {
  initialize();
  writeStored(THEME_STORAGE_KEY, preference);
  update({
    preference,
    // 切回 `system` 时立刻取一次真实值，否则会先闪一帧旧结果
    systemPrefersDark: preference === 'system' ? systemPrefersDarkNow() : state.systemPrefersDark,
  });
}

export function setThemeContrast(contrast: ContrastPreference): void {
  initialize();
  writeStored(CONTRAST_STORAGE_KEY, contrast);
  update({ contrast });
}

export function resolvedThemeOf(current: ThemeState): ResolvedTheme {
  return resolveTheme(current.preference, current.systemPrefersDark);
}
